// The handle on the line between two columns (list | detail, chat | side
// view): drag it to change the left column's width, double-click it to go back
// to the default. The sidebar's edge is the shell's own (`SidebarEdge` in
// me-shell.tsx: it also folds the sidebar), drawn with the same line.
//
// Promise:
// - An 8px band centered on the line, looking like the line already there; it
//   darkens on hover and while held. The cursor says it resizes.
// - Dragging starts from the width shown now (even when the stored width was
//   squeezed), follows the pointer outside the band (pointer capture), stays in
//   [min, max], and reports every new width (`onChange`, not stored). Letting go
//   reports the last one (`onCommit`, stored by the caller); a double click
//   commits `resetTo`.
// - No keyboard (the old web's choice: not needed).
import { useRef, useState } from "react";

export function Splitter({
  at,
  width,
  min,
  max,
  resetTo,
  onChange,
  onCommit,
  lineOffset = 4,
  lineInset = 0,
  label,
}: {
  /** The x of the line, from the left of the positioned box around. */
  at: number;
  /** The left column's width now: the drag starts from it. */
  width: number;
  min: number;
  max: number;
  /** The width a double click goes back to. */
  resetTo: number;
  /** While dragging (not stored). */
  onChange(width: number): void;
  /** On release or double click (store it). */
  onCommit(width: number): void;
  /** Where the line to darken is, from the band's left (over the line there). */
  lineOffset?: number;
  /** Keeps the line off the top and bottom (round corners of a panel). */
  lineInset?: number;
  /** A name for tests and tools (the band itself is hidden from readers). */
  label: string;
}) {
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; width: number } | null>(null);
  const latest = useRef(width);
  if (!dragging) latest.current = width;
  const clamp = (w: number) => Math.max(min, Math.min(max, Math.round(w)));
  const end = () => {
    if (!start.current) return;
    start.current = null;
    setDragging(false);
    onCommit(latest.current);
  };
  return (
    <div
      aria-hidden="true"
      data-splitter={label}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        start.current = { x: e.clientX, width };
        latest.current = width;
        setDragging(true);
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }}
      onPointerMove={(e) => {
        const s = start.current;
        if (!s) return;
        const next = clamp(s.width + (e.clientX - s.x));
        if (next === latest.current) return;
        latest.current = next;
        onChange(next);
      }}
      onPointerUp={(e) => {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        end();
      }}
      onPointerCancel={end}
      onDoubleClick={() => onCommit(clamp(resetTo))}
      className="group absolute inset-y-0 z-20 w-2 cursor-col-resize touch-none"
      style={{ left: at - 4 }}
    >
      <span
        style={{ left: lineOffset, top: lineInset, bottom: lineInset }}
        className={`absolute w-px ${
          dragging ? "bg-field-strong" : "group-hover:bg-field-strong/60"
        }`}
      />
    </div>
  );
}
